import type { UnitMapping } from '../types/anchoredRowTypes'
import type { CompleteMicroSegmentationResult } from '../types/segmentationTypes'
import {
  buildLedgerSafeWitnessContextAnalysis,
  type LedgerSafePreparedWitnessContext,
  type LedgerSafeWitnessContextAnalysis
} from './ledgerSafeBoundaryContext'
import {
  auditLedgerSafeWitnessEditabilityFromAnalysis,
  type LedgerSafeWitnessEditabilityAudit
} from './ledgerSafeBoundaryEditability'
import {
  deriveLedgerSafeUnassignedFragments,
  type LedgerSafeUnassignedFragment
} from './ledgerSafeBoundaryReconcile'
import type { LedgerSafeRowProjection } from './ledgerSafeRowProjection'
import {
  buildLedgerSafeSequenceAudit,
  type LedgerSafeSequenceAudit
} from './ledgerSafeSequenceAudit'

export interface LedgerSafeActiveWitnessProjectionWitness {
  id: string
  segmentation_result?: CompleteMicroSegmentationResult | null
  uploaded_file?: {
    content?: string | null
  } | null
}

export interface LedgerSafeActiveWitnessProjection {
  preparedContext: LedgerSafePreparedWitnessContext | null
  rawFragments: LedgerSafeUnassignedFragment[]
  sequenceAudit: LedgerSafeSequenceAudit | null
  editabilityAudit: LedgerSafeWitnessEditabilityAudit | null
}

export interface LedgerSafeActiveWitnessProjectionState {
  rowProjection: LedgerSafeRowProjection
  witnessId: string | null
  readOnly: boolean
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  sourceText: string
  projection: LedgerSafeActiveWitnessProjection
}

export interface LedgerSafeActiveWitnessProjectionBuilders {
  buildWitnessContextAnalysis: typeof buildLedgerSafeWitnessContextAnalysis
  deriveUnassignedFragments: typeof deriveLedgerSafeUnassignedFragments
  buildSequenceAudit: typeof buildLedgerSafeSequenceAudit
  auditEditabilityFromAnalysis: typeof auditLedgerSafeWitnessEditabilityFromAnalysis
}

interface LedgerSafeActiveWitnessProjectionEventBase {
  scopeKey: string
  witnessId: string | null
  contentRevision: number
  orderRevision: number
}

export type LedgerSafeActiveWitnessProjectionBuildEvent =
  | (LedgerSafeActiveWitnessProjectionEventBase & {
      type: 'prepare-context'
    })
  | (LedgerSafeActiveWitnessProjectionEventBase & {
      type: 'projection-built'
      preparedContextBuilds: 0 | 1
    })
  | (LedgerSafeActiveWitnessProjectionEventBase & {
      type: 'projection-reused'
      preparedContextBuilds: 0
    })

export type LedgerSafeActiveWitnessProjectionObserver = (
  event: LedgerSafeActiveWitnessProjectionBuildEvent
) => void

const DEFAULT_BUILDERS: LedgerSafeActiveWitnessProjectionBuilders = {
  buildWitnessContextAnalysis: buildLedgerSafeWitnessContextAnalysis,
  deriveUnassignedFragments: deriveLedgerSafeUnassignedFragments,
  buildSequenceAudit: buildLedgerSafeSequenceAudit,
  auditEditabilityFromAnalysis: auditLedgerSafeWitnessEditabilityFromAnalysis
}

const haveSameIds = (
  left: readonly string[],
  right: readonly string[]
): boolean => (
  left.length === right.length
  && left.every((id, index) => id === right[index])
)

const getFirstActiveMapping = (
  rowProjection: LedgerSafeRowProjection,
  rowId: string,
  witnessId: string
): UnitMapping | null => (
  rowProjection.rowById
    .get(rowId)
    ?.unitMappings
    .find(mapping => mapping.witnessId === witnessId && !mapping.excluded)
  || null
)

const haveSemanticallyEqualActiveMappings = (
  left: UnitMapping | null,
  right: UnitMapping | null
): boolean => (
  left === right
  || (
    Boolean(left) === Boolean(right)
    && left?.witnessId === right?.witnessId
    && left?.unitUuid === right?.unitUuid
    && left?.unitLabel === right?.unitLabel
    && left?.textSnapshot === right?.textSnapshot
    && left?.translationStatus === right?.translationStatus
    && left?.transpositionOriginRowId === right?.transpositionOriginRowId
    && Boolean(left?.excluded) === Boolean(right?.excluded)
  )
)

const haveSameActiveWitnessInputsAcrossChangedRows = ({
  previous,
  current,
  witnessId
}: {
  previous: LedgerSafeRowProjection
  current: LedgerSafeRowProjection
  witnessId: string | null
}): boolean => {
  return Array.from(current.changedRowIds).every(rowId => {
    const previousRow = previous.rowById.get(rowId)
    const currentRow = current.rowById.get(rowId)
    if (
      !previousRow
      || !currentRow
      || previousRow.position !== currentRow.position
      || previousRow.labelNumber !== currentRow.labelNumber
    ) {
      return false
    }
    if (!witnessId) {
      return true
    }
    return haveSemanticallyEqualActiveMappings(
      getFirstActiveMapping(previous, rowId, witnessId),
      getFirstActiveMapping(current, rowId, witnessId)
    )
  })
}

const canReuseProjection = ({
  previous,
  rowProjection,
  witnessId,
  readOnly,
  segmentationResult,
  sourceText
}: {
  previous: LedgerSafeActiveWitnessProjectionState
  rowProjection: LedgerSafeRowProjection
  witnessId: string | null
  readOnly: boolean
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  sourceText: string
}): boolean => {
  const previousRows = previous.rowProjection
  if (
    previousRows.scopeKey !== rowProjection.scopeKey
    || previous.witnessId !== witnessId
    || previous.readOnly !== readOnly
    || previous.segmentationResult !== segmentationResult
    || previous.sourceText !== sourceText
    || !haveSameIds(previousRows.rowIds, rowProjection.rowIds)
    || !haveSameIds(previousRows.structuralRowIds, rowProjection.structuralRowIds)
    || rowProjection.orderRevision !== previousRows.orderRevision
  ) {
    return false
  }

  const contentRevisionDelta = rowProjection.contentRevision - previousRows.contentRevision
  if (contentRevisionDelta < 0 || contentRevisionDelta > 1) {
    return false
  }

  return haveSameActiveWitnessInputsAcrossChangedRows({
    previous: previousRows,
    current: rowProjection,
    witnessId
  })
}

const buildEventBase = (
  rowProjection: LedgerSafeRowProjection,
  witnessId: string | null
): LedgerSafeActiveWitnessProjectionEventBase => ({
  scopeKey: rowProjection.scopeKey,
  witnessId,
  contentRevision: rowProjection.contentRevision,
  orderRevision: rowProjection.orderRevision
})

export const reconcileLedgerSafeActiveWitnessProjection = ({
  previous = null,
  rowProjection,
  witness,
  readOnly,
  builders: builderOverrides,
  observer
}: {
  previous?: LedgerSafeActiveWitnessProjectionState | null
  rowProjection: LedgerSafeRowProjection
  witness: LedgerSafeActiveWitnessProjectionWitness | null | undefined
  readOnly: boolean
  builders?: Partial<LedgerSafeActiveWitnessProjectionBuilders>
  observer?: LedgerSafeActiveWitnessProjectionObserver
}): LedgerSafeActiveWitnessProjectionState => {
  const witnessId = witness?.id || null
  const segmentationResult = witness?.segmentation_result
  const sourceText = String(witness?.uploaded_file?.content || '')
  const eventBase = buildEventBase(rowProjection, witnessId)

  if (
    previous
    && canReuseProjection({
      previous,
      rowProjection,
      witnessId,
      readOnly,
      segmentationResult,
      sourceText
    })
  ) {
    observer?.({
      ...eventBase,
      type: 'projection-reused',
      preparedContextBuilds: 0
    })
    if (previous.rowProjection === rowProjection) {
      return previous
    }
    return {
      ...previous,
      rowProjection
    }
  }

  const builders: LedgerSafeActiveWitnessProjectionBuilders = {
    ...DEFAULT_BUILDERS,
    ...builderOverrides
  }
  const rows = Array.from(rowProjection.rows)
  let analysis: LedgerSafeWitnessContextAnalysis | null = null
  let preparedContextBuilds: 0 | 1 = 0

  if (!readOnly && witnessId && segmentationResult && sourceText) {
    preparedContextBuilds = 1
    observer?.({
      ...eventBase,
      type: 'prepare-context'
    })
    analysis = builders.buildWitnessContextAnalysis({
      rows,
      witnessId,
      segmentationResult,
      sourceText
    })
  }

  const projection: LedgerSafeActiveWitnessProjection = {
    preparedContext: analysis?.preparedContext || null,
    rawFragments: !readOnly && witnessId && segmentationResult
      ? builders.deriveUnassignedFragments({
          witnessId,
          segmentationResult,
          rows
        })
      : [],
    sequenceAudit: !readOnly && witnessId && segmentationResult
      ? builders.buildSequenceAudit({
          rows,
          witnessId,
          segmentationResult
        })
      : null,
    editabilityAudit: analysis
      ? builders.auditEditabilityFromAnalysis({
          analysis,
          segmentationResult,
          sourceText
        })
      : null
  }

  observer?.({
    ...eventBase,
    type: 'projection-built',
    preparedContextBuilds
  })

  return {
    rowProjection,
    witnessId,
    readOnly,
    segmentationResult,
    sourceText,
    projection
  }
}
